import { defineStore } from 'pinia';
import { useProductStore } from '@/stores/product';

/**
 * État VDP (Variable Data Printing / publipostage) de la CRÉATION courante.
 *
 * Modèle MULTI-CHAMPS : jusqu'à {@link MAX_VDP_FIELDS} champs variables par ligne
 * de variation. Les données sont un TABLEAU (lignes × colonnes) ; chaque colonne
 * est un « champ » nommé, et chaque champ peut être posé LIBREMENT sur le canvas
 * comme un élément texte indépendant (lié à sa colonne via `options.vdp.columnId`).
 *
 * Chaque LIGNE du tableau = un exemplaire imprimé où TOUS les champs posés sont
 * substitués. Le nombre de lignes = nombre de pages du PDF variable = quantité
 * commandée (facturation par ligne, PAS par champ).
 *
 * Persistance : la liaison élément→colonne vit dans `element.options.vdp.columnId`,
 * et le tableau (colonnes + lignes) est miroité dans `options.vdp.table` de chaque
 * élément lié (pour survivre au save/reload). Ce store est la source runtime.
 */

export interface VdpColumnStyle {
    fontFamily: string;
    fontSize: number;
    fill: string;
}

export interface VdpColumn {
    id: string;
    name: string;
    /** Style propre au champ (appliqué à l'élément posé de cette colonne). */
    style?: VdpColumnStyle;
}

export function defaultColumnStyle(): VdpColumnStyle {
    return { fontFamily: 'Arial', fontSize: 40, fill: '#000000' };
}

export const MAX_VDP_FIELDS = 4;

let seq = 0;
function newColumnId(): string {
    seq += 1;
    // Date.now est disponible côté navigateur.
    return `vdpc_${Date.now().toString(36)}_${seq}`;
}

export const useVdpStore = defineStore('vdp', {
    state: () => ({
        /** Colonnes = champs variables nommés (jusqu'à MAX_VDP_FIELDS). */
        columns: [] as VdpColumn[],
        /** Lignes de variation : chaque ligne = { columnId: valeur }. */
        rows: [] as Record<string, string>[],
        /** Liaison élément canvas (uuid) -> colonne. */
        fields: {} as Record<string, string>,
        /** Index de la ligne actuellement prévisualisée. */
        index: 0
    }),
    getters: {
        maxFields: (): number => MAX_VDP_FIELDS,
        canAddColumn: (state): boolean => state.columns.length < MAX_VDP_FIELDS,
        columnCount: (state): number => state.columns.length,
        /** Nombre de lignes = nombre de pages du PDF variable (facturation). */
        lineCount: (state): number => state.rows.length,
        /**
         * Plafond du nombre de lignes en e-commerce = stock de la variante
         * sélectionnée (cf. productStore.vdpMaxRows). null => aucun plafond.
         */
        maxRows(): number | null {
            return useProductStore().vdpMaxRows;
        },
        /** Peut-on encore ajouter une ligne ? (plafond stock e-commerce non atteint) */
        canAddRow(state): boolean {
            const max = this.maxRows;
            return max == null || state.rows.length < max;
        },
        /** Ids des éléments canvas liés à une colonne. */
        boundObjectIds: (state): string[] => Object.keys(state.fields),
        /** VDP actif = au moins une colonne, une ligne et un élément lié. */
        hasVdp: (state): boolean =>
            state.columns.length > 0 && state.rows.length > 0 && Object.keys(state.fields).length > 0,
        currentRow: (state): Record<string, string> => state.rows[state.index] ?? {},
        columnName:
            (state) =>
            (columnId: string): string =>
                state.columns.find((c) => c.id === columnId)?.name ?? '',
        valueForColumn:
            (state) =>
            (columnId: string): string =>
                state.rows[state.index]?.[columnId] ?? '',
        columnForObject:
            (state) =>
            (objectId: string): string | null =>
                state.fields[objectId] ?? null
    },
    actions: {
        /** Valeur courante à afficher pour un élément lié (ligne prévisualisée). */
        valueForObject(objectId: string): string {
            const columnId = this.fields[objectId];
            if (!columnId) {
                return '';
            }
            return this.rows[this.index]?.[columnId] ?? '';
        },

        // --- Colonnes (champs) ---
        addColumn(name?: string, style?: VdpColumnStyle): VdpColumn | null {
            if (this.columns.length >= MAX_VDP_FIELDS) {
                return null;
            }
            const col: VdpColumn = {
                id: newColumnId(),
                name: name || `Champ ${this.columns.length + 1}`,
                style: style ? { ...style } : defaultColumnStyle()
            };
            this.columns.push(col);
            this.rows.forEach((r) => {
                if (!(col.id in r)) {
                    r[col.id] = '';
                }
            });
            return col;
        },
        renameColumn(id: string, name: string) {
            const c = this.columns.find((c) => c.id === id);
            if (c) {
                c.name = name;
            }
        },
        setColumnStyle(id: string, patch: Partial<VdpColumnStyle>) {
            const c = this.columns.find((c) => c.id === id);
            if (c) {
                c.style = { ...(c.style ?? defaultColumnStyle()), ...patch };
            }
        },
        columnStyle(id: string): VdpColumnStyle {
            return this.columns.find((c) => c.id === id)?.style ?? defaultColumnStyle();
        },
        removeColumn(id: string) {
            this.columns = this.columns.filter((c) => c.id !== id);
            this.rows.forEach((r) => delete r[id]);
            for (const [oid, cid] of Object.entries(this.fields)) {
                if (cid === id) {
                    delete this.fields[oid];
                }
            }
        },

        // --- Lignes (données) ---
        addRow() {
            // Plafond e-commerce : on ne dépasse pas le stock de la variante (le
            // nombre de lignes = quantité imprimée). No-op si le plafond est atteint.
            if (!this.canAddRow) {
                return;
            }
            const r: Record<string, string> = {};
            this.columns.forEach((c) => {
                r[c.id] = '';
            });
            this.rows.push(r);
        },
        removeRow(rowIndex: number) {
            if (rowIndex < 0 || rowIndex >= this.rows.length) {
                return;
            }
            this.rows.splice(rowIndex, 1);
            if (this.index >= this.rows.length) {
                this.index = Math.max(0, this.rows.length - 1);
            }
        },
        updateCell(rowIndex: number, columnId: string, value: string) {
            const row = this.rows[rowIndex];
            if (row) {
                row[columnId] = String(value ?? '');
            }
        },

        /**
         * Importe un tableau (en-têtes + données) : crée/complète jusqu'à
         * MAX_VDP_FIELDS colonnes (nommées par les en-têtes) et remplace les lignes.
         * Réutilise les colonnes existantes PAR POSITION (garde leurs ids => les
         * liaisons élément→colonne restent valides), en les renommant.
         */
        importTable(headers: string[], data: string[][]) {
            trackOnce('feature_used', { feature: 'vdp' }, 'feature:vdp');
            trackEvent('vdp_table_imported', {
                columns: (headers || []).length,
                rows: (data || []).length
            });
            const cols = (headers || []).slice(0, MAX_VDP_FIELDS);
            const nextColumns: VdpColumn[] = [];
            for (let i = 0; i < cols.length; i++) {
                const existing = this.columns[i];
                const name = String(cols[i] ?? `Champ ${i + 1}`).trim() || `Champ ${i + 1}`;
                if (existing) {
                    existing.name = name;
                    nextColumns.push(existing);
                } else {
                    nextColumns.push({ id: newColumnId(), name });
                }
            }
            this.columns = nextColumns;
            // Plafond e-commerce : on TRONQUE l'import au stock de la variante (on ne
            // peut pas imprimer plus d'exemplaires que le stock). null => pas de limite.
            const max = this.maxRows;
            const limitedData = max != null ? (data || []).slice(0, max) : data || [];
            this.rows = limitedData.map((line) => {
                const row: Record<string, string> = {};
                nextColumns.forEach((c, i) => {
                    row[c.id] = String(line?.[i] ?? '').trim();
                });
                return row;
            });
            const validIds = new Set(nextColumns.map((c) => c.id));
            for (const [oid, cid] of Object.entries(this.fields)) {
                if (!validIds.has(cid)) {
                    delete this.fields[oid];
                }
            }
            this.index = 0;
        },

        // --- Liaisons élément ↔ colonne ---
        bindObject(objectId: string, columnId: string) {
            if (!objectId || !columnId) {
                return;
            }
            this.fields[objectId] = columnId;
        },
        unbindObject(objectId: string) {
            delete this.fields[objectId];
        },

        // --- Navigation lignes ---
        setIndex(i: number) {
            if (!this.rows.length) {
                this.index = 0;
                return;
            }
            const n = this.rows.length;
            this.index = ((i % n) + n) % n;
        },
        next() {
            this.setIndex(this.index + 1);
        },
        prev() {
            this.setIndex(this.index - 1);
        },

        /**
         * Réhydrate le store depuis les éléments du moteur (chargement design/panier).
         * `entries` = { objectId, columnId, table } lus sur options.vdp. Le tableau
         * est pris du 1er élément qui le porte.
         */
        hydrate(
            entries: Array<{
                objectId: string;
                columnId?: string;
                table?: { columns: VdpColumn[]; rows: Record<string, string>[] };
            }>
        ) {
            this.clear();
            const withTable = entries.find((e) => e.table && Array.isArray(e.table.columns));
            if (withTable?.table) {
                this.columns = withTable.table.columns.slice(0, MAX_VDP_FIELDS).map((c) => ({ ...c }));
                this.rows = (withTable.table.rows || []).map((r) => ({ ...r }));
            }
            const validIds = new Set(this.columns.map((c) => c.id));
            for (const e of entries) {
                if (e.objectId && e.columnId && validIds.has(e.columnId)) {
                    this.fields[e.objectId] = e.columnId;
                }
            }
            this.index = 0;
        },

        clear() {
            this.columns = [];
            this.rows = [];
            this.fields = {};
            this.index = 0;
        }
    }
});
